import chroma from 'chroma-js'

/**
 * Generate a full Chakra UI color palette (50-950) from a base color
 * The base color is used as the 500 shade (median color)
 *
 * Uses two different algorithms depending on the lightness of the base color:
 * - DARK base (lightness < 40%): boosts lighter shades, preserves darker ones
 * - LIGHT base (lightness ≥ 40%): preserves lighter shades, boosts darker ones
 *
 * @param primaryColor - The primary color in hex format (e.g., '#065F46')
 * @returns An object with shades from 50 to 950
 */
export function generateColorPalette(primaryColor: string) {
  try {
    const base = chroma(primaryColor)
    const [h, s, l] = base.hsl()

    // Convert percentages to 0-100 scale for easier calculation
    const sPercent = s * 100
    const lPercent = l * 100

    // Helper to create color from HSL values
    const createShade = (hue: number, saturation: number, lightness: number) => {
      // Clamp values to valid ranges
      const clampedS = Math.max(0, Math.min(100, saturation))
      const clampedL = Math.max(0, Math.min(100, lightness))
      return chroma.hsl(hue, clampedS / 100, clampedL / 100).hex()
    }

    // Choose algorithm based on base lightness
    if (lPercent < 40) {
      // 🌙 DARK BASE ALGORITHM (lightness < 40%)
      // Boosts lighter shades significantly, preserves darker tones
      return {
        50: createShade(h, sPercent * 0.65, 97),
        100: createShade(h, sPercent * 0.70, 93),
        200: createShade(h, sPercent * 0.80, 87),
        300: createShade(h, sPercent * 0.90, 72),
        400: createShade(h, sPercent * 0.95, lPercent * 2.2),
        500: primaryColor, // Base color
        600: createShade(h, sPercent * 0.97, lPercent * 0.80),
        700: createShade(h, sPercent * 0.92, lPercent * 0.65),
        800: createShade(h, sPercent * 0.85, lPercent * 0.50),
        900: createShade(h, sPercent * 0.77, lPercent * 0.40),
        950: createShade(h, sPercent * 0.70, lPercent * 0.30),
      }
    } else {
      // ☀️ LIGHT BASE ALGORITHM (lightness ≥ 40%)
      // Preserves lighter shades, boosts darker tones
      return {
        50: createShade(h, sPercent * 0.70, 98),
        100: createShade(h, sPercent * 0.75, 95),
        200: createShade(h, sPercent * 0.80, 90),
        300: createShade(h, sPercent * 0.85, 80),
        400: createShade(h, sPercent * 0.92, lPercent + (100 - lPercent) * 0.40),
        500: primaryColor, // Base color
        600: createShade(h, sPercent * 1.05, lPercent - lPercent * 0.20),
        700: createShade(h, sPercent * 1.08, lPercent - lPercent * 0.35),
        800: createShade(h, sPercent * 1.05, lPercent - lPercent * 0.50),
        900: createShade(h, sPercent * 0.95, lPercent - lPercent * 0.65),
        950: createShade(h, sPercent * 0.85, lPercent - lPercent * 0.75),
      }
    }
  } catch (error) {
    console.error('Error generating color palette:', error)
    // Fallback to default Kopro green palette
    return {
      50: '#F0FDF9',
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399',
      500: '#065F46',
      600: '#054C38',
      700: '#043A2A',
      800: '#032920',
      900: '#021F18',
      950: '#011410',
    }
  }
}
